package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.unit.Dp
import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.ui.chats.SearchDisplay
import com.swetdot.swetgram.ui.chats.failuresCoincide
import com.swetdot.swetgram.ui.chats.previewLine
import com.swetdot.swetgram.ui.chats.rowTimestamp
import com.swetdot.swetgram.ui.chats.searchDisplay
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetAvatarFace
import com.swetdot.swetgram.ui.design.SwetBadgeStyle
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetGroupAvatar
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIconButton
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetListRow
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetPullToRefresh
import com.swetdot.swetgram.ui.design.SwetScreenTitle
import com.swetdot.swetgram.ui.design.SwetSearchBar
import com.swetdot.swetgram.ui.design.chatRowDividerIndent
import com.swetdot.swetgram.ui.design.swetFloatingNavClearance
import com.swetdot.swetgram.ui.design.usesGroupAvatarGrid
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.ui.text.text
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chatlist_row_muted_description
import swetgram.shared.generated.resources.chatlist_search_empty_title
import swetgram.shared.generated.resources.chatlist_search_placeholder
import swetgram.shared.generated.resources.groups_list_create_description
import swetgram.shared.generated.resources.groups_list_empty_body
import swetgram.shared.generated.resources.groups_list_empty_title
import swetgram.shared.generated.resources.groups_list_new_group
import swetgram.shared.generated.resources.groups_list_search_empty_body
import swetgram.shared.generated.resources.groups_list_title
import swetgram.shared.generated.resources.handle_prefixed
import swetgram.shared.generated.resources.value_passthrough
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * The Groups tab — SDS §27.8 and SPS §12, DRAWN AS THE CHAT LIST.
 *
 * THE USER'S INSTRUCTION WAS "on groups to be like a conversation list chats like chats", and the
 * mockup is the chat-list mockup with group data in it. So this screen is now the same object as
 * `ui/chats/ChatListScreen`, and almost none of it is new code:
 *
 *  - **Pure black, edge to edge.** `background.deep`, no app-bar `Surface`, no divider under the
 *    title — the canvas §25 rebuilt the conversation onto and §26 moved the inbox onto.
 *  - **A screen title, not a toolbar label.** `headlineLarge` 28sp bold on the canvas with ONE
 *    action beside it, and that action is the same square-and-pencil `SwetIcons.Compose` the Chats
 *    tab uses. See [GroupsHeader], and see below for what moved into it.
 *  - **The mockup's search field:** [SwetSearchBar] at `Radius.field`, drawn only when there is
 *    something behind it. See `GroupsUiState.canSearch`.
 *  - **The mockup's row:** [SwetListRow] with the indented hairline, a 52dp `avatarChatRow` disc or
 *    `SwetGroupAvatar`'s 2×2 member grid, a sender-prefixed preview, a timestamp on the first line
 *    and `SwetBadgeStyle.Inverted`'s white disc with a black number on the second. Every one of
 *    those is the design system's or the chat list's; see [GroupRow], which assembles and does not
 *    invent.
 *
 * WHERE THE ROW'S DATA COMES FROM, because it is the one genuinely new idea here. The contract's
 * `Group` carries a name, a description, a member count and a role — and none of the five things a
 * chat-list row is made of. Those belong to the CONVERSATION underneath the group, which this client
 * already caches for the Chats tab, and `Group.conversationId` is the key into it. `GroupsViewModel`
 * joins the two; this screen renders the join and falls back to the group's own subtitle for a group
 * whose conversation is not cached (see [groupPreviewLine]). No endpoint, no repository method and
 * no second read were added for it.
 *
 * WHAT MOVED, AND WHAT WAS DROPPED TO GET HERE — stated rather than left to be noticed:
 *
 *  - **The create-group FAB IS GONE**, along with `CreateFabSize`, `AnimatedCreateGroupFab`,
 *    `CreateGroupFab` and its `PlusGlyph`. Creating a group is the HEADER's glyph now, matching
 *    Chats exactly, which is what the mockup shows. Deleted rather than hidden behind a flag, for
 *    the reason `ChatListScreen` gives about its own pair: a `visible = false` disc is a decision
 *    left ajar. The empty state keeps its "New group" button, so a brand-new account still has two
 *    ways to its first group.
 *  - **`fabStackHeight` is no longer imported.** It reserved room for the disc that is gone. What
 *    the list reserves for now is the floating TAB BAR, exactly as the inbox does, so the black runs
 *    to the bottom edge with the rows scrolling under it — see [GroupList], and see the two lines in
 *    `ui/Navigation.kt` that have to move with it.
 *
 * WHAT DID NOT CHANGE IS MOST OF THE FILE'S VALUE. The four states of SDS §25 and §29 are exactly as
 * they were: skeletons on a first read with nothing cached, §17.2's own words when a read SETTLED
 * empty (`showsEmptyState`, never `isEmpty`), an error banner ABOVE whatever is already on screen,
 * pull-to-refresh, and the paging footer that fires on scroll. A layout rewrite is the easiest place
 * in the world to lose a week of behaviour.
 */
@Composable
fun GroupsScreen(
    state: GroupsUiState,
    modifier: Modifier = Modifier,
    /**
     * Opens a group. The WHOLE `Group` is handed back rather than an id, because a group has two of
     * them and they are different types for a reason — `GroupId` addresses the group, `ChatId` its
     * chat. Which one the caller wants depends on where it routes, and this row should not guess.
     *
     * IT IS ALSO WHAT A SEARCH RESULT OPENS, and that is worth stating: `globalSearch` returns
     * public groups the caller may not belong to, so this lambda can now be handed a group whose
     * detail screen will answer `GroupFailure.UNAVAILABLE`. That renders as "This group is no longer
     * available.", which is not the right sentence for "you are not in it". The contract has no join
     * without an invite token, so there is no better destination in this build; it is recorded here
     * rather than hidden.
     */
    onOpenGroup: ((Group) -> Unit)? = null,
    /**
     * SPS §12.2: "Groups tab via create group button." Null draws no header action rather than an
     * inert one — the same rule `ChatListScreen.onNewChat` follows for the identical glyph.
     */
    onCreateGroup: (() -> Unit)? = null,
    /** Null hides the banner's action: an error nobody can act on should not offer a button. */
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    /** SDS §2.1: the list is refreshable by pull. Null hides the gesture rather than faking it. */
    onRefresh: (() -> Unit)? = null,
    /** Null shows a single page on purpose — for a preview, or a caller that does not page. */
    onLoadMore: (() -> Unit)? = null,
    /** SPS §14.2's group search. Inert by default; the FIELD is gated on `state.canSearch`. */
    onQueryChange: (String) -> Unit = {},
    /** Runs the current query again. Null draws no Retry rather than an inert one. */
    onRetrySearch: (() -> Unit)? = null,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    // Read once. Recomputing it on every recomposition would make the whole list unstable, and the
    // only thing it decides is "today or not" — which a refresh re-evaluates anyway.
    val now = remember { Clock.System.now() }

    // The two failure channels, spelled once, here, because this is the first composable scope they
    // reach. Each stays null when there is nothing to say, so a screen with no failure does no
    // resource lookup at all. Resolved BEFORE `failuresCoincide` sees them, deliberately: that
    // function compares the SENTENCE, because two failures the design system chose to describe
    // identically are, on screen, one fact.
    val errorText = state.error.resolveOrNull()
    val searchErrorText = state.searchError.resolveOrNull()

    // The mockup's first line, and the inbox's: pure black, edge to edge.
    //
    // The strip BEHIND THE STATUS BAR is the app frame's, not this screen's, and it is still
    // `colors.background` for this route — `ui/Navigation.kt`'s `frameBackground` names the
    // conversation and the Chats tab and nothing else. See the note in `GroupList` about the pair of
    // one-line changes there that finish the canvas; neither file was this pass's to edit.
    Column(modifier.fillMaxSize().background(SwetgramThemeTokens.colors.backgroundDeep)) {
        // WAS `SwetTopAppBar(title = "Groups")` with its divider. The mockup has no app-bar surface
        // and no divider: a large screen title sitting on the canvas with one action beside it.
        GroupsHeader(
            title = stringResource(Res.string.groups_list_title),
            onCompose = onCreateGroup,
        )

        // GATED, not defaulted on. A box that swallows what is typed and answers nothing would have
        // to render SOMETHING underneath it, and every option is a claim nobody asked the server
        // about. `GroupsUiState.canSearch` says why this is the same rule as a null action.
        if (state.canSearch) {
            SwetSearchBar(
                value = state.query,
                onValueChange = onQueryChange,
                // REUSED, not twinned. The chat list's placeholder is the same one word in the same
                // shaped field; two keys for it is how the app started reading half-translated.
                placeholder = stringResource(Res.string.chatlist_search_placeholder),
                // The mockup: "a ROUNDED RECTANGLE, not a pill". See `Radius.field`.
                cornerRadius = Radius.field,
                modifier = Modifier.padding(
                    start = Space.screenMargin,
                    end = Space.screenMargin,
                    bottom = Space.one,
                ),
            )
        }

        // The list's failure and the search's, RECONCILED rather than stacked — the chat list's own
        // rule, reused rather than re-derived. Offline, both requests come back with the same
        // sentence and the screen would draw it twice, one banner directly under the other.
        val oneBannerForBoth = failuresCoincide(errorText, searchErrorText, state.isSearching)

        // Above the list, never instead of it: a failed refresh must not blank the groups the user
        // already has. That is exactly what `SwetErrorBanner` is — a banner and not a screen.
        if (errorText != null) {
            SwetErrorBanner(
                message = errorText,
                // When this banner is speaking for the search as well, its Retry has to run both
                // requests — otherwise the merged banner would half-work, refreshing the list and
                // leaving the results underneath it still missing.
                onRetry = if (oneBannerForBoth) {
                    retryBoth(onRetry, onRetrySearch)
                } else {
                    onRetry
                },
                onDismiss = onDismissError,
            )
        }
        // The SEARCH's own failure, and only while a search is on screen: it is about the results
        // below it, and it would be a non-sequitur over the list of groups the user belongs to.
        // Suppressed only when the banner above is already saying this exact sentence.
        if (state.isSearching && searchErrorText != null && !oneBannerForBoth) {
            SwetErrorBanner(message = searchErrorText, onRetry = onRetrySearch)
        }

        SwetPullToRefresh(
            refreshing = state.refreshing,
            // Suppressed while searching: the results below are a query's answer, not the tab, and
            // pulling on them would refetch something the user is not looking at.
            onRefresh = onRefresh?.takeIf { !state.isSearching },
            modifier = Modifier.weight(1f).fillMaxWidth(),
        ) {
            when {
                state.isSearching -> GroupSearchResults(
                    results = state.results,
                    conversations = state.conversations,
                    me = state.me,
                    now = now,
                    zone = zone,
                    searching = state.searching,
                    settled = state.searchSettled,
                    failed = searchErrorText != null,
                    onOpenGroup = onOpenGroup,
                )

                // Nothing read yet. Skeletons rather than a spinner, so the real rows land where the
                // placeholders were instead of the screen reflowing under the user (§2.1).
                //
                // `avatarSize` IS THE HALF THAT PROMISE NEEDS. The row draws `avatarChatRow` now, so
                // a skeleton left on the 44dp default would start its text column 8dp to the left of
                // the row it stands in for and the whole list would twitch sideways on resolve.
                state.isFirstLoad -> Column(
                    Modifier.fillMaxSize().padding(bottom = swetFloatingNavClearance()),
                ) {
                    repeat(SKELETON_ROWS) { SwetListRowSkeleton(avatarSize = Sizes.avatarChatRow) }
                }

                // `showsEmptyState`, not `isEmpty`: a first read that FAILED must not be reported as
                // a community you have no part in. See `GroupsUiState.showsEmptyState`.
                state.showsEmptyState -> SwetEmptyState(
                    modifier = Modifier.padding(bottom = swetFloatingNavClearance()),
                    title = stringResource(Res.string.groups_list_empty_title),
                    // §17.2 continues "Create one or join with a link." and SPS §19.3 says the same.
                    // THE SECOND HALF IS STILL WITHHELD — there is no link handler in this build and
                    // the sentence would send somebody looking for a control that does not exist. It
                    // goes back verbatim when invite links land.
                    body = stringResource(Res.string.groups_list_empty_body),
                    actionText = if (onCreateGroup != null) {
                        stringResource(Res.string.groups_list_new_group)
                    } else {
                        null
                    },
                    onAction = onCreateGroup,
                    // SDS §10.3 asks for an illustration on exactly two surfaces, and the empty
                    // Groups screen is one of them.
                    illustration = { GroupsMark() },
                )

                else -> GroupList(
                    groups = state.groups,
                    conversations = state.conversations,
                    me = state.me,
                    now = now,
                    zone = zone,
                    hasMore = state.hasMore,
                    onOpenGroup = onOpenGroup,
                    onLoadMore = onLoadMore,
                )
            }

            // NOTHING FLOATS HERE ANY MORE. `AnimatedCreateGroupFab` stood at `BottomEnd` in this Box
            // and is deleted along with the disc it drew; the create action is the header's glyph.
        }
    }
}

/** Enough placeholder rows to fill a phone screen; more would only ever be scrolled past. */
private const val SKELETON_ROWS = 7

private const val SEARCH_SKELETON_ROWS = 3

private const val PAGING_FOOTER_KEY = "groups-paging-footer"

// ---------------------------------------------------------------------------------------------
// The lists
// ---------------------------------------------------------------------------------------------

@Composable
private fun GroupList(
    groups: List<Group>,
    conversations: Map<String, Chat>,
    me: UserId?,
    now: Instant,
    zone: TimeZone,
    hasMore: Boolean,
    onOpenGroup: ((Group) -> Unit)?,
    onLoadMore: (() -> Unit)?,
    modifier: Modifier = Modifier,
) {
    LazyColumn(
        modifier = modifier.fillMaxSize(),
        /*
         * THE LIST RESERVES ROOM FOR THE FLOATING TAB BAR ITSELF, exactly as the inbox does, so the
         * mockup's black runs to the bottom edge with the rows scrolling under the bar.
         *
         * THIS IS ONE THIRD OF AN ATOMIC CHANGE and the other two thirds are in `ui/Navigation.kt`:
         * `Tab.Groups.route` is in `frameBackground` there, and in `barOverlapsContent`. Without the
         * second of those the host ALSO pads this route and the two reservations stack into a 64dp
         * hole under the last row; with it and without this line the last group hides under the bar.
         * Neither half is separately correct.
         *
         * `swetFloatingNavClearance()` is COMPUTED from the bar's own geometry -- its two insets, its
         * height, and the runtime gesture-bar inset -- rather than picked. Three screens picked a
         * number for a floating thing by hand and all three were wrong; `fabStackHeight` carries that
         * history. The create-group FAB this list used to reserve for is gone into the header, so
         * this is the only thing left to reserve for.
         */
        contentPadding = PaddingValues(bottom = swetFloatingNavClearance()),
    ) {
        itemsIndexed(items = groups, key = { _, group -> group.id.value }) { index, group ->
            GroupRow(
                group = group,
                chat = conversations[group.conversationId.value],
                me = me,
                now = now,
                zone = zone,
                // Between rows, not under the last one: a hairline under the final row is a rule
                // across the bottom of the list with nothing beneath it. The paging footer counts
                // as "something beneath it", which is why `hasMore` is in this test.
                showDivider = hasMore || index < groups.lastIndex,
                onClick = onOpenGroup?.let { open -> { open(group) } },
            )
        }

        // The paging footer, driven by scrolling into view rather than by a button. Keyed on the
        // current size so it re-runs once per page: keyed on `Unit` it would fire once and never
        // again, and the list would stop at page two.
        if (hasMore) {
            item(key = PAGING_FOOTER_KEY) {
                LaunchedEffect(groups.size) { onLoadMore?.invoke() }
                SwetListRowSkeleton(avatarSize = Sizes.avatarChatRow)
            }
        }
    }
}

/**
 * SPS §14.2's `Groups` result group, on its own screen rather than as a section.
 *
 * The three states are the chat list's, through the chat list's own [searchDisplay] — imported
 * rather than rewritten, because the ORDER inside that function is the whole rule and a second copy
 * of it is a second place to get it backwards. FAILED comes before EMPTY: a lookup that did not run
 * says nothing at all about which groups exist, and "No results found." is a statement about the
 * server's records that a client with no answer may not make. That is the exact defect fixed on the
 * chat list two days ago, and it is not re-derived here.
 */
@Composable
private fun GroupSearchResults(
    results: List<Group>,
    conversations: Map<String, Chat>,
    me: UserId?,
    now: Instant,
    zone: TimeZone,
    searching: Boolean,
    settled: Boolean,
    /** The lookup failed. A banner above says so; this must not also claim emptiness. */
    failed: Boolean,
    onOpenGroup: ((Group) -> Unit)?,
    modifier: Modifier = Modifier,
) {
    when (
        searchDisplay(
            hasResults = results.isNotEmpty(),
            searching = searching,
            settled = settled,
            failed = failed,
        )
    ) {
        // Still looking. Skeletons rather than "no results", which would be a wrong answer shown
        // confidently for as long as the request takes.
        SearchDisplay.Loading -> Column(
            modifier.fillMaxSize().padding(bottom = swetFloatingNavClearance()),
        ) {
            repeat(SEARCH_SKELETON_ROWS) { SwetListRowSkeleton(avatarSize = Sizes.avatarChatRow) }
        }

        // Nothing is drawn. The banner above is the whole statement, and it is the only one this
        // screen is entitled to make.
        SearchDisplay.Failed -> Unit

        // SDS §17.2's search empty state, and only over an ANSWER. The TITLE is the chat list's own
        // key — one sentence, one key — and only the second line is this screen's, because the chat
        // list's advice is about names and usernames and a group has neither.
        SearchDisplay.Empty -> SwetEmptyState(
            title = stringResource(Res.string.chatlist_search_empty_title),
            body = stringResource(Res.string.groups_list_search_empty_body),
            modifier = modifier.padding(bottom = swetFloatingNavClearance()),
        )

        // THE ONE THAT WAS WRONG ON EVERY DEVICE, not merely on a short window. This list reserved
        // NOTHING, so the last group in a search result sat under the floating bar always and in
        // both orientations. `ChatListScreen`'s equivalent results list has always reserved the
        // clearance; the Groups copy of that screen never picked it up, and the two screens looking
        // identical is exactly what stopped anyone noticing.
        SearchDisplay.Results -> LazyColumn(
            modifier = modifier.fillMaxSize(),
            contentPadding = PaddingValues(bottom = swetFloatingNavClearance()),
        ) {
            itemsIndexed(items = results, key = { _, group -> "s:${group.id.value}" }) { index, group ->
                GroupRow(
                    group = group,
                    // A public group the caller has never opened has no cached conversation, so most
                    // rows here fall back to the group's own subtitle — which is the right line for a
                    // result anyway: the handle, the size and the description are what identify a
                    // group you are choosing between, and the last message is what identifies one you
                    // are already in.
                    chat = conversations[group.conversationId.value],
                    me = me,
                    now = now,
                    zone = zone,
                    showDivider = index < results.lastIndex,
                    onClick = onOpenGroup?.let { open -> { open(group) } },
                )
            }
        }
    }
}

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

/** Where the mockup's hairline starts. Derived from the avatar; see [chatRowDividerIndent]. */
private val RowDividerIndent: Dp = chatRowDividerIndent(Sizes.avatarChatRow)

/**
 * One group, as a chat-list row.
 *
 * The TITLE is the group's name rather than `Chat.displayTitle`, and that is not arbitrary: a group
 * is renamed through `updateGroup`, the conversation's title mirrors it, and the group is the
 * resource this screen actually read. Preferring the cached conversation's copy would show a stale
 * name for as long as the cache had one.
 *
 * Everything else on the row is the conversation's, because none of it exists on a `Group`. A group
 * with no cached conversation still draws a complete row — see [groupPreviewLine].
 */
@Composable
private fun GroupRow(
    group: Group,
    chat: Chat?,
    me: UserId?,
    now: Instant,
    zone: TimeZone,
    /**
     * Draw the mockup's hairline under this row.
     *
     * NOT DEFAULTED, for the reason `ChatListScreen.ChatRow` states at length: three call sites emit
     * this row and each has a different answer for "is this the last one". A default of `true` would
     * put a rule across the bottom of every list; a default of `false` would silently drop the
     * dividers from whichever list somebody forgot.
     */
    showDivider: Boolean,
    onClick: (() -> Unit)?,
) {
    val colors = SwetgramThemeTokens.colors

    SwetListRow(
        title = group.name,
        preview = groupPreviewLine(group, chat, me).resolve(),
        // `rowTimestamp` is the chat list's, which is `dayLabel` -> `DayLabel` -> `dayLabelText`
        // with the one thing that is true of a list column and nothing else: a row from TODAY shows
        // a clock. There is exactly ONE relative-date rule in this app and this is not a second one.
        timestamp = chat?.lastMessage?.let { rowTimestamp(it.sentAt, now, zone).resolve() },
        unreadCount = chat?.unreadCount ?: 0,
        // The mockup: "a CIRCLE with a WHITE FILL and a BLACK number in it". The timestamp above it
        // follows the same style by construction — see `SwetListRow`.
        badgeStyle = SwetBadgeStyle.Inverted,
        // SPS §10.3 / SDS §12.1: a muted conversation is marked on the row. It carries its own
        // description because it is the only thing that says so — the unread badge still shows, and
        // without this a muted group and a noisy one are indistinguishable.
        titleTrailing = if (chat?.isMuted == true) {
            {
                SwetIcon(
                    icon = SwetIcons.BellOff,
                    tint = colors.textSecondary,
                    size = Sizes.iconSmall,
                    // Reused rather than twinned: the same bell on the same row shape.
                    contentDescription = stringResource(Res.string.chatlist_row_muted_description),
                )
            }
        } else {
            null
        },
        dividerIndent = if (showDivider) RowDividerIndent else null,
        onClick = onClick,
    ) {
        if (drawsGroupMemberGrid(group, chat)) {
            SwetGroupAvatar(
                // Mapped here rather than in the design system: `ui/design` does not know what a
                // `User` is, and `SwetListRow`'s own KDoc is where that rule is written down.
                faces = remember(chat) {
                    chat?.members.orEmpty().map { SwetAvatarFace(it.displayName, it.avatarUrl) }
                },
                size = Sizes.avatarChatRow,
                // The hairline between the four tiles takes the colour of what the disc sits on,
                // exactly as the online dot's border does. That is the black canvas here.
                surfaceColor = colors.backgroundDeep,
            )
        } else {
            SwetAvatar(
                displayName = group.name,
                size = Sizes.avatarChatRow,
                surfaceColor = colors.backgroundDeep,
                // §14.1's group identity: an uploaded image, or generated initials. `SwetAvatar`
                // draws the initials first and the photo over them, so there is no empty circle
                // while it loads and nothing to undo if it never arrives.
                avatarUrl = group.avatarUrl,
                // A GROUP IS NOT A PERSON. Presence belongs to users; a green dot here would assert
                // something about a room. The description is nulled with it so a screen reader is
                // not told about an "Online" state that is never drawn.
                isOnline = false,
                onlineContentDescription = null,
            )
        }
    }
}

/**
 * Does this row draw the four-face grid instead of a single disc?
 *
 * A rule rather than a condition inline in the row, so it can be asserted on a host with no
 * renderer. Two things it deliberately does NOT do:
 *
 *  - It does not consult `Group.memberCount`, which is the server's figure for the whole group. The
 *    FACES come from the cached conversation, so the count that decides whether a grid can be filled
 *    has to be the number of faces actually in hand. A 40-member group with two cached members draws
 *    what `usesGroupAvatarGrid` says two faces are worth, not what forty would be.
 *  - It does not draw a grid for a group with no cached conversation at all, because there are no
 *    faces to put in it. That row gets the group's avatar or its initials, which is a complete
 *    answer rather than an empty circle.
 *
 * The member threshold and the "a group with its own picture keeps it" rule are both stated on
 * [usesGroupAvatarGrid], which is where the reasoning lives.
 */
internal fun drawsGroupMemberGrid(group: Group, chat: Chat?): Boolean =
    chat != null && usesGroupAvatarGrid(group.avatarUrl, chat.members.size)

/**
 * The row's second line: the conversation's last message, or the group's own summary.
 *
 * THE PREFERENCE IS THE POINT, and it is what makes this tab read as the chat list. A group the user
 * is IN has a last message, and "Данил: Я отправил файлы" is what tells them whether to open it. A
 * group they are merely looking at has no conversation in the cache, and the handle, the size and
 * the description are what identify it. So the fallback is not a degradation — it is the right line
 * for the row that gets it.
 *
 * `previewLine` is the chat list's, imported rather than rewritten: it is eight keys deep, it
 * unwraps the self-speaker through `chatlist_preview_speaker_self` rather than a literal, and a
 * second copy would be a second place for a Russian phone to start reading English.
 *
 * [me] IS REQUIRED FOR IT AND MAY BE NULL. `Chat.preview` decides "Me:" versus a member's name and
 * cannot answer without knowing who "I" am, so a null one falls back rather than guessing: naming
 * the wrong person on a row is worse than naming nobody. A conversation with NO last message falls
 * back too, for a plainer reason — `previewLine` would say "No messages yet", and the group's
 * subtitle says more.
 */
internal fun groupPreviewLine(group: Group, chat: Chat?, me: UserId?): UiText =
    if (chat?.lastMessage != null && me != null) {
        previewLine(chat, me)
    } else {
        groupRowSubtitle(group)
    }

/**
 * One Retry for a banner that speaks for two requests, or null when neither can be re-run.
 *
 * Null rather than an empty lambda: [SwetErrorBanner] draws no action for a null `onRetry`, and a
 * button that provably does nothing is worse than no button.
 */
private fun retryBoth(onRetry: (() -> Unit)?, onRetrySearch: (() -> Unit)?): (() -> Unit)? {
    if (onRetry == null && onRetrySearch == null) return null
    return {
        onRetry?.invoke()
        onRetrySearch?.invoke()
    }
}

/** The separator between the parts of a row's second line. U+00B7, with hair space either side. */
private const val ROW_SEPARATOR = " · "

/**
 * The group's own one-line summary: the handle, the size, and what the group says it is.
 *
 * DELIBERATELY NOT `groupSubtitle`, which the group DETAIL header uses, and the difference is worth
 * stating because the two are one keystroke from being merged. `groupSubtitle` leads with "Public
 * group" / "Private group", which is exactly right under a group's name on its own screen and is
 * noise repeated down a list of groups that are mostly the same kind. It also has no room for the
 * description, which SDS §14.1 names as part of the CARD specifically. So the row shares
 * `memberCountLine` — the piece that must not have two spellings — and composes its own line.
 *
 * ONE ADDITION AND ONE OMISSION against §14.1, both stated rather than left to be noticed:
 *
 *  - The public @HANDLE is shown, which §14.1 does not list. That is a decision. Its cost of being
 *    absent is concrete: a public group's username is chosen once, on the create screen, and putting
 *    it on the row is what makes it visible from the tab rather than only from the group.
 *  - LAST ACTIVITY is not shown, because the contract's `Group` does not carry it. §14.1 offers
 *    "last activity OR description", and the description is the half that exists. NOTE WHAT CHANGED
 *    AROUND THIS: the re-skinned row does show last activity now, from the cached CONVERSATION, and
 *    this line is what a row without one falls back to. See [groupPreviewLine].
 *
 * ORDER IS THE POINT. `SwetListRow` clips this to one line, so whatever is last is what a long entry
 * gives up first — and the description is the part that can be four hundred characters.
 *
 * `publicAddress` rather than the visibility is what decides the handle: `domain/Group.kt` folds the
 * contract's `username` and `public_url` into one value precisely so that "is this shareable" is a
 * single question, and a private group has none.
 */
/*
 * A UiText.Joined, because the middle part is a plural and the last part is a VALUE: the description
 * is whatever the group's owner typed, so it travels through `value_passthrough` rather than as a
 * bare string. See UiText for why there is no Raw case to reach for.
 */
internal fun groupRowSubtitle(group: Group): UiText = UiText.Joined(
    buildList {
        group.publicAddress?.username?.takeIf { it.isNotBlank() }
            ?.let { add(Res.string.handle_prefixed.text(it)) }
        add(memberCountLine(group.memberCount))
        group.description?.trim()?.takeIf { it.isNotEmpty() }
            ?.let { add(Res.string.value_passthrough.text(it)) }
    },
    ROW_SEPARATOR,
)

// ---------------------------------------------------------------------------------------------
// The header
// ---------------------------------------------------------------------------------------------

/**
 * The mockup's header: the screen's name, large, on the canvas, with one action beside it.
 *
 * IT IS `SwetScreenTitle` NOW and no longer a private `Row`. This screen, the inbox and the Discover
 * feed had three private headers with the same shape, the same 28sp role and the same
 * `defaultMinSize` — a minimum whose whole job is to keep the three tabs' titles on one baseline
 * when a user switches between them, which is a property no one of the three files could hold. Two
 * of them also derived `HeaderActionInset` privately and identically.
 *
 * THE GLYPH IS THE CHATS TAB'S, and it is in `ui/design` now rather than reached for across a
 * package: `SwetIcons.Compose` is the square with a pencil through it, and the mockup puts the SAME
 * mark in the SAME place on this screen. A second hand-rolled copy of a 24dp `Canvas` mark is how
 * two tabs end up with two subtly different pencils.
 *
 * The action is null-safe: no `onCompose` draws no button, which is the same rule the deleted
 * floating action followed and the reason a host that cannot create a group does not offer to. The
 * end padding follows it — see `SwetScreenTitle`.
 */
@Composable
private fun GroupsHeader(title: String, onCompose: (() -> Unit)?) {
    // Hoisted: `SwetIconButton` takes the description as a plain String parameter, and the resource
    // lookup is composable.
    val composeDescription = stringResource(Res.string.groups_list_create_description)

    SwetScreenTitle(
        title = title,
        action = onCompose?.let {
            {
                SwetIconButton(contentDescription = composeDescription, onClick = it) { tint ->
                    SwetIcon(icon = SwetIcons.Compose, tint = tint, contentDescription = null)
                }
            }
        },
    )
}

// ---------------------------------------------------------------------------------------------
// Empty-state illustration
// ---------------------------------------------------------------------------------------------

/**
 * 96dp: two steps of §6.1's 8dp scale above the 64dp tab bar, and the same diameter as the profile
 * avatar — the largest circle the app draws. Sized from the scale rather than picked.
 */
private val GroupsMarkSize: Dp = Space.eight + Space.four

/**
 * An overlapping stack of three, the ordinary idiom for a group of people.
 *
 * §10.2 asks for minimal, calm, and the brand colours used subtly, and its first "do not" is
 * overloading an empty state with large art — so this is a mark, not artwork. Circles are verifiable
 * by eye and survive both themes; §10 hands real illustration to the brand specialist and none are
 * bundled yet.
 *
 * Colours: `card` for the disc and `accent` for the mark. `accentSoft` is the obvious fill and is a
 * trap — pale #DBEAFE in light, saturated #1D4ED8 in dark, so a disc of it becomes a blue slab at
 * night with the mark nearly invisible on it.
 *
 * TODO(design-system): this is the same mark `ui/UnbuiltSectionScreen.kt` draws for the Groups tab
 * it no longer serves. The two are never on screen together — this screen replaces that one — but
 * the shape belongs in one place. That file was not this pass's to change.
 *
 * Static: §19.4 rules out decorative loops, and nothing here is waiting on anything. Semantics are
 * cleared by `SwetEmptyState`, which treats whatever it is handed as decoration.
 */
@Composable
private fun GroupsMark() {
    val colors = SwetgramThemeTokens.colors
    val disc = colors.card
    val mark = colors.accent

    Canvas(Modifier.size(GroupsMarkSize)) {
        val w = size.width
        val h = size.height
        drawCircle(disc, radius = w * 0.5f, center = Offset(w * 0.5f, h * 0.5f))

        val radius = w * 0.135f
        val ring = w * 0.035f
        val step = w * 0.19f
        // Right to left, so the leftmost ends up on top and each knockout lands over a circle that
        // has already been drawn. Without the ring, three circles in a row read as an ellipsis.
        for (index in 2 downTo 0) {
            val centre = Offset(w * 0.5f + (index - 1) * step, h * 0.5f)
            drawCircle(disc, radius = radius + ring, center = centre)
            drawCircle(mark, radius = radius, center = centre)
        }
    }
}
